import type { ReactNode } from 'react';
import { Chip } from './bits';
import { Icon } from './Icon';
import type { IconName } from './icons';

// Setup guides: numbered steps into a provider's console. Each step has a title, what to do, the link that
// opens the right page, then anything to paste or fill in.

/** A link that opens a provider's console in a new tab. */
export function ExtLink({
  href,
  children,
  className = 'btn btn-sm btn-tile',
  icon = 'external',
}: {
  href: string;
  children: ReactNode;
  className?: string;
  icon?: IconName;
}) {
  return (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer">
      <Icon name={icon} />
      {children}
      <span className="sr"> (opens in a new tab)</span>
    </a>
  );
}

/** A guide in its own tile: the title with how many steps are done, an optional intro, then the steps. */
export function Guide({
  title,
  done,
  total,
  intro,
  aside,
  headingId,
  className,
  children,
}: {
  title: string;
  done: number;
  total: number;
  intro?: ReactNode;
  /** Extra controls on the title line, after the count. */
  aside?: ReactNode;
  headingId?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={['tile', className].filter(Boolean).join(' ')}>
      <div className="tile-h">
        <h3 id={headingId}>{title}</h3>
        {done === total ? (
          <Chip tone="success" icon="check">
            Completed
          </Chip>
        ) : (
          <span className="cnt">
            {done} of {total} done
          </span>
        )}
        {aside}
      </div>
      {intro ? <p className="guide-intro">{intro}</p> : null}
      {children}
    </div>
  );
}

/** The numbered list of steps. */
export function GuideSteps({ children, id, labelledBy, className }: { children: ReactNode; id?: string; labelledBy?: string; className?: string }) {
  return (
    <ol className={['guide', className].filter(Boolean).join(' ')} id={id} aria-labelledby={labelledBy}>
      {children}
    </ol>
  );
}

/** One step: done steps show a tick in place of their number. */
export function GuideStep({
  done,
  title,
  sub,
  action,
  children,
}: {
  done?: boolean;
  title?: ReactNode;
  /** What to do, in muted text under the title. */
  sub?: ReactNode;
  /** The step's link or button, under the explanation. */
  action?: ReactNode;
  /** Fields, code to paste or values to copy, last. */
  children?: ReactNode;
}) {
  return (
    <li className={done ? 'gstep done' : 'gstep'}>
      <div className="grow">
        {title ? <b className="gstep-t">{title}</b> : null}
        {sub ? <div className="gstep-sub">{sub}</div> : null}
        {action ? <div className="gstep-acts">{action}</div> : null}
        {children ? <div className="gstep-in">{children}</div> : null}
      </div>
      {done ? <span className="sr">Done</span> : null}
    </li>
  );
}
